import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import PasswordInput from '@/components/PasswordInput';
import { Mail } from 'lucide-react';
import ProfileSetupWizard from '@/components/onboarding/ProfileSetupWizard';
import { BEHAVIOURAL_QUESTIONS } from '@/utils/behavioural';

const BACKEND_URL = '';
// Use relative URLs for auth — avoids cross-origin CORS issues on production mobile
const API = '/api';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [invitePrefill, setInvitePrefill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linkedInAvailable, setLinkedInAvailable] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [registrationSent, setRegistrationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  // Check for britelab invite token in URL
  const urlParams = new URLSearchParams(window.location.search);
  const britelabInviteToken = urlParams.get('britelab_invite') || '';
  const communityInviteToken = urlParams.get('community_invite') || '';
  const [showRegister, setShowRegister] = useState(!!britelabInviteToken || !!communityInviteToken);

  useEffect(() => {
    fetch(`${API}/auth/config`)
      .then(res => res.json())
      .then(data => setLinkedInAvailable(data.linkedin_oauth))
      .catch(() => setLinkedInAvailable(false));
  }, []);

  // Pre-fill registration form when arriving via a community invite link
  useEffect(() => {
    if (!communityInviteToken) return;
    fetch(`${API}/community-invites/${communityInviteToken}`)
      .then(r => r.ok ? r.json() : null)
      .then(inv => {
        if (!inv) return;
        setInvitePrefill({ email: inv.invitee_email || '', name: inv.invitee_name || '', organisation: inv.invitee_company || '' });
        toast.success(`${inv.inviter_name} invited you to join — your details have been pre-filled.`);
      })
      .catch(() => {});
  }, [communityInviteToken]);

  const safeNavigate = (path, userData) => {
    // Honour a deep-link the user was trying to reach before they hit the
    // login wall. Two sources, in priority order:
    //   1. ?next=... query param on the current /login URL (set by
    //      App.js when it forced this redirect)
    //   2. sessionStorage `post_login_redirect` fallback (older flow)
    // Both are only honoured when LoginPage's caller asked to go to
    // /dashboard — i.e. the regular post-login destination — so 2FA and
    // registration flows keep their explicit paths intact.
    let target = path;
    let usedDeepLink = false;
    try {
      const search = new URLSearchParams(window.location.search);
      const nextParam = search.get('next') || '';
      const stashed = sessionStorage.getItem('post_login_redirect') || '';
      const candidate = nextParam || stashed;
      if (candidate && path === '/progress' && candidate.startsWith('/') && !candidate.startsWith('//') && !candidate.startsWith('/login')) {
        target = candidate;
        usedDeepLink = true;
        sessionStorage.removeItem('post_login_redirect');
      }
    } catch (e) {}
    // For deep-link restores we use a full document load so the destination
    // page sees the path AND its search params exactly as they appear in
    // the URL. The session cache has already been populated by handleLogin
    // so the destination won't have to re-authenticate.
    if (usedDeepLink) {
      window.location.href = target;
      return;
    }
    try {
      const safeUser = JSON.parse(JSON.stringify(userData || {}));
      navigate(target, { state: { user: safeUser } });
    } catch {
      window.location.href = target;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Login failed');
      }
      const data = await response.json();
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setShow2FA(true);
        return;
      }
      if (data.force_password_change) {
        if (data.session_token) {
          sessionStorage.setItem('session_token', data.session_token);
          localStorage.setItem('sll_session_token', data.session_token);
        }
        toast.error('You must change your password before continuing.');
        navigate('/reset-password?forced=true');
        return;
      }
      if (data.session_token) {
        sessionStorage.setItem('session_token', data.session_token);
        localStorage.setItem('sll_session_token', data.session_token);
      }
      if (data.user) {
        const userStr = JSON.stringify(data.user);
        sessionStorage.setItem('sll_user', userStr);
        localStorage.setItem('sll_user', userStr);
      }
      toast.success('Welcome back!');
      setTimeout(() => safeNavigate('/progress', data.user), 100);
    } catch (error) {
      toast.error(error.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ temp_token: tempToken, code: totpCode })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Verification failed');
      }
      const data = await response.json();
      if (data.session_token) {
        sessionStorage.setItem('session_token', data.session_token);
        localStorage.setItem('sll_session_token', data.session_token);
      }
      if (data.user) {
        const userStr = JSON.stringify(data.user);
        sessionStorage.setItem('sll_user', userStr);
        localStorage.setItem('sll_user', userStr);
      }
      toast.success('Welcome back!');
      setTimeout(() => safeNavigate('/progress', data.user), 100);
    } catch (error) {
      toast.error(error.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (v, photo) => {
    const picture = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(photo);
    });
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: v.email.trim(), password: v.password, name: v.name.trim(),
          job_title: v.role.trim(), organisation: v.organisation.trim(), location: v.location.trim(),
          stakeholder_type: v.background, country: v.country, region: v.region, sector: v.sector,
          forum_topics: v.forum_topics_of_interest, picture,
          behavioural: Object.fromEntries(BEHAVIOURAL_QUESTIONS.map(q => [q.key, v[q.key]])),
          britelab_invite_token: britelabInviteToken || undefined, community_invite_token: communityInviteToken || undefined,
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Registration failed');
      }
      const data = await response.json();
      if (data.requires_verification) {
        setRegisteredEmail(v.email.trim());
        setRegistrationSent(true);
        toast.success('Registration received! You will be notified once your account is approved.');
        return;
      }
      if (data.session_token) {
        sessionStorage.setItem('session_token', data.session_token);
        localStorage.setItem('sll_session_token', data.session_token);
      }
      if (data.user) {
        const userStr = JSON.stringify(data.user);
        sessionStorage.setItem('sll_user', userStr);
        localStorage.setItem('sll_user', userStr);
      }
      toast.success('Account created successfully!');
      setTimeout(() => safeNavigate('/progress', data.user), 100);
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    }
  };

  // Detect in-app webviews where Google OAuth is blocked
  const isInAppBrowser = (() => {
    const ua = navigator.userAgent || '';
    // Standard webview detection
    if (/FBAN|FBAV|Instagram|Line\/|Twitter|Snapchat|wv\)|WebView/i.test(ua)) return true;
    // iOS: no standalone Safari (in-app browser lacks full Safari markers)
    if (/iPhone|iPad/i.test(ua) && !navigator.standalone) {
      // In-app browsers on iOS often lack "Safari" or have it but also other markers
      if (ua.includes('CriOS') || ua.includes('FxiOS') || ua.includes('EdgiOS')) return false; // Real browsers
      if (!ua.includes('Safari/')) return true; // No Safari token = webview
    }
    return false;
  })();

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/progress';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;

    if (isInAppBrowser) {
      // Try opening in system browser
      const opened = window.open(authUrl, '_system') || window.open(authUrl, '_blank');
      if (!opened) {
        navigator.clipboard?.writeText(window.location.origin + '/login').then(() => {
          toast.info('Google Sign-In requires Safari or Chrome. URL copied — paste it in your browser to continue.', { duration: 8000 });
        }).catch(() => {
          toast.info('Google Sign-In requires Safari or Chrome. Please open this page in your browser.', { duration: 8000 });
        });
      }
      return;
    }

    window.location.href = authUrl;
  };

  const handleLinkedInLogin = async () => {
    try {
      const response = await fetch(`${API}/auth/linkedin`, { credentials: 'include' });
      if (!response.ok) throw new Error('LinkedIn not configured');
      const data = await response.json();
      window.location.href = data.auth_url;
    } catch {
      toast.error('LinkedIn login is not configured yet. Please use email/password or Google.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail, origin_url: window.location.origin }),
      });
      setForgotSent(true);
    } catch { toast.error('Something went wrong'); }
    finally { setLoading(false); }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const res = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Verification email resent!');
    } catch (e) { toast.error(e.message); }
    finally { setResending(false); }
  };

  const GoogleIcon = () => (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );

  const LinkedInIcon = () => (
    <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );

  const SocialButtons = ({ prefix }) => (
    <>
      <div className="my-5 relative">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
        <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-400">Or</span></div>
      </div>
      <div className="space-y-2.5">
        <Button data-testid={`google-${prefix}-button`} onClick={handleGoogleLogin} variant="outline"
          className="w-full h-11 text-sm border-slate-200 hover:bg-slate-50">
          <GoogleIcon /> Continue with Google
        </Button>
        {isInAppBrowser && (
          <p className="text-[11px] text-amber-600 text-center px-2">
            Google Sign-In may not work in this browser. If blocked, open this page in Safari or Chrome.
          </p>
        )}
        {linkedInAvailable && (
          <Button data-testid={`linkedin-${prefix}-button`} onClick={handleLinkedInLogin}
            className="w-full h-11 bg-[#0A66C2] hover:bg-[#004182] text-white text-sm">
            <LinkedInIcon /> Continue with LinkedIn
          </Button>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel — Landing page style */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-white">
        {/* Same hero-bg as landing page */}
        <img src="/assets/hero-bg.png" alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.45 }} />
        {/* White fade from right for blend into form */}
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(to right, rgba(255,255,255,0) 40%, rgba(255,255,255,0.4) 70%, rgba(255,255,255,0.95) 100%)' }} />
        {/* Top white fade */}
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, white 0%, rgba(255,255,255,0) 20%)' }} />
        {/* Bottom white fade */}
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, white 0%, rgba(255,255,255,0) 25%)' }} />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo + Header */}
          <div className="flex items-center gap-3">
            <img src="/assets/britehub-logo.png" alt="SLL BriteHub" className="h-10 w-10" />
            <span className="text-2xl font-bold tracking-tight text-[#121E30]">SLL BriteHub</span>
          </div>

          {/* Hero text — matching landing page */}
          <div className="max-w-md">
            <h2 className="text-3xl sm:text-4xl font-bold leading-[1.15] tracking-tight text-[#121E30] mb-4">
              Connecting the global sustainable logistics community
            </h2>
            <p className="text-base text-slate-500 leading-relaxed max-w-sm">
              Join SLL BriteHub to collaborate, share knowledge and drive impactful action for a sustainable future
            </p>
          </div>

          {/* Footer */}
          <div className="pt-6 border-t border-slate-200/60">
            <p className="text-slate-400 text-xs">&copy; 2026 Shape Tomorrow Limited. Company registration 16752504.</p>
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-white px-6 py-10">
        <div className="w-full max-w-md">
          {/* Mobile logo (hidden on desktop where left panel shows it) */}
          <div className="mb-8">
            <div className="flex items-center gap-2.5 mb-3 lg:hidden">
              <img src="/assets/britehub-logo.png" alt="SLL BriteHub" className="h-8 w-8" />
              <span className="text-xl font-bold tracking-tight text-[#121E30]">SLL BriteHub</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading gradient-text mb-1.5">SLL BriteHub</h1>
            <p className="text-sm text-slate-500">
              {show2FA ? 'Enter your authenticator code' : showForgot ? 'Reset your password' : 'Welcome back. Sign in to continue.'}
            </p>
          </div>

          {/* Session-expired banner — set by the global 401 interceptor
              when a stale cookie sends us back to /login. Explains the
              "empty screen" mystery members often report on mobile. */}
          {(() => {
            try {
              const p = new URLSearchParams(window.location.search);
              return p.get('expired') === '1';
            } catch { return false; }
          })() && !show2FA && !showForgot && (
            <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="session-expired-banner">
              For your security we signed you out after a period of inactivity. Please sign in again to pick up where you left off.
            </div>
          )}

          {registrationSent ? (
            <div className="text-center py-6" data-testid="registration-check-email">
              <div className="w-14 h-14 bg-brand-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-7 w-7 text-brand-sage" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Check your email</h3>
              <p className="text-sm text-slate-500 mb-6">
                We've sent a verification link to <strong>{registeredEmail}</strong>. Click the link in the email to activate your account.
              </p>
              <Button
                data-testid="resend-verification-btn"
                variant="outline"
                disabled={resending}
                onClick={handleResendVerification}
                className="mb-3"
              >
                {resending ? 'Sending...' : 'Resend Verification Email'}
              </Button>
              <br />
              <button
                type="button"
                data-testid="back-to-login-from-verify"
                onClick={() => { setRegistrationSent(false); setRegisteredEmail(''); }}
                className="text-sm text-brand-navy hover:text-brand-dark font-medium mt-2"
              >
                Back to Log In
              </button>
            </div>
          ) : show2FA ? (
            <div data-testid="2fa-login-form">
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-brand-navy/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="h-7 w-7 text-brand-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-500">Open your authenticator app and enter the 6-digit code.</p>
              </div>
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <Input data-testid="2fa-login-code" type="text" inputMode="numeric" maxLength={6}
                  placeholder="000000" value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                  className="h-12 text-center text-xl tracking-[0.5em] font-mono border-slate-200 focus:ring-2 focus:ring-brand-blue/20" />
                <Button data-testid="2fa-login-submit" type="submit" disabled={loading || totpCode.length !== 6}
                  className="w-full h-11 bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white">
                  {loading ? 'Verifying...' : 'Verify'}
                </Button>
                <button type="button" onClick={() => { setShow2FA(false); setTotpCode(''); setTempToken(''); }}
                  className="w-full text-sm text-brand-navy hover:text-brand-dark font-medium">
                  Back to Login
                </button>
              </form>
            </div>
          ) : showForgot ? (
            <div data-testid="forgot-password-form">
              {forgotSent ? (
                <div className="text-center py-8" data-testid="forgot-password-sent">
                  <div className="w-14 h-14 bg-brand-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="h-7 w-7 text-brand-sage" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Check your email</h3>
                  <p className="text-sm text-slate-500 mb-6">If an account exists for <strong>{forgotEmail}</strong>, we've sent a password reset link.</p>
                  <Button data-testid="back-to-login-btn" variant="outline" onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }}>
                    Back to Log In
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-slate-500">Enter your email and we'll send you a link to reset your password.</p>
                  <Input data-testid="forgot-email-input" type="email" placeholder="Email address" value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)} required
                    className="h-11 border-slate-200 focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue" />
                  <Button data-testid="forgot-submit-btn" type="submit" disabled={loading}
                    className="w-full h-11 text-sm bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white">
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                  <button type="button" data-testid="back-to-login-btn" onClick={() => setShowForgot(false)}
                    className="w-full text-sm text-brand-navy hover:text-brand-dark font-medium">
                    Back to Log In
                  </button>
                </form>
              )}
            </div>
          ) : (
          <Tabs defaultValue={showRegister ? 'register' : 'login'} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100">
              <TabsTrigger data-testid="login-tab" value="login">Log In</TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <Input data-testid="login-email-input" type="email" placeholder="Email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required
                  className="h-11 border-slate-200 focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue" />
                <PasswordInput data-testid="login-password-input" placeholder="Password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  className="h-11 border-slate-200 focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue pr-10" />
                <Button data-testid="login-submit-button" type="submit" disabled={loading}
                  className="w-full h-11 text-sm bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white">
                  {loading ? 'Logging in...' : 'Log In'}
                </Button>
                <div className="text-center">
                  <button type="button" data-testid="forgot-password-link" onClick={() => setShowForgot(true)}
                    className="text-sm text-brand-navy hover:text-brand-dark font-medium">
                    Forgot your password?
                  </button>
                </div>
              </form>
              <SocialButtons prefix="login" />
            </TabsContent>

            <TabsContent value="register">
              <ProfileSetupWizard key={invitePrefill ? 'invite' : 'blank'} mode="signup" initial={invitePrefill || {}}
                terms={{ agreed: agreedToTerms, setAgreed: setAgreedToTerms, open: () => setShowTerms(true) }}
                onSubmit={handleRegister} submitLabel="Create Account" />
              <SocialButtons prefix="register" />
            </TabsContent>
          </Tabs>
          )}

          {/* Mobile-only copyright */}
          <p className="lg:hidden text-center text-xs text-slate-400 mt-8">
            &copy; 2026 Shape Tomorrow Limited. Company registration 16752504.
          </p>
        </div>
      </div>

      {/* T&C Dialog */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Terms & Conditions</DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm text-slate-600 space-y-4">
            <p className="text-xs text-slate-400">Last updated: March 2026</p>
            <h3 className="text-base font-semibold text-slate-800">1. Acceptance of Terms</h3>
            <p>By creating an account on the Sustainable Logistics Leaders (SLL) Community Portal, you agree to be bound by these Terms & Conditions, our Privacy Policy, and our GDPR Data Processing Agreement.</p>
            <h3 className="text-base font-semibold text-slate-800">2. Privacy Policy</h3>
            <p>We collect and process personal data including your name, email address, organisation, job title, professional interests, and profile information you provide. This data is used to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide and personalise the community experience</li>
              <li>Enable smart networking and content recommendations</li>
              <li>Send relevant notifications and communications</li>
              <li>Facilitate event registrations and course access</li>
            </ul>
            <h3 className="text-base font-semibold text-slate-800">3. GDPR Compliance</h3>
            <p>In accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Lawful Basis:</strong> We process your data based on your consent (provided by accepting these terms) and legitimate interest in operating the community platform.</li>
              <li><strong>Data Controller:</strong> Shape Tomorrow Ltd is the data controller for your personal information.</li>
              <li><strong>Your Rights:</strong> You have the right to access, rectify, erase, restrict processing, data portability, and object to processing of your personal data.</li>
              <li><strong>Data Retention:</strong> Your data is retained for as long as your account is active. You may request deletion at any time.</li>
              <li><strong>Data Security:</strong> We implement appropriate technical and organisational measures to protect your personal data.</li>
            </ul>
            <h3 className="text-base font-semibold text-slate-800">4. Use of the Platform</h3>
            <p>You agree to use the platform responsibly and not to share misleading information, engage in harassment, or use the platform for purposes unrelated to sustainable logistics.</p>
            <h3 className="text-base font-semibold text-slate-800">5. Premium Content & Payments</h3>
            <p>Certain content (courses, keynotes) may require a paid subscription. Payments are processed securely via Stripe. Subscription terms and refund policies are detailed at the point of purchase.</p>
            <h3 className="text-base font-semibold text-slate-800">6. Intellectual Property</h3>
            <p>All content on the platform, including course materials, keynote slides, and articles, is the property of SLL or its content contributors and may not be reproduced without permission.</p>
            <h3 className="text-base font-semibold text-slate-800">7. Contact</h3>
            <p>For any data protection queries or to exercise your rights, please contact us at <strong>privacy@shapetomorrow.co.uk</strong>.</p>
          </div>
          <div className="pt-4 border-t">
            <Button data-testid="accept-terms-btn" onClick={() => { setAgreedToTerms(true); setShowTerms(false); }}
              className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white">
              I Accept
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
